import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { ActionPlanService } from "../api/ActionPlanService";
import { SurveyService } from "../api/SurveyService";
import { ActionPlanOverview } from "../components/ActionPlanOverview/ActionPlanOverview";
import { ActionPlanSetupSteps } from "../components/ActionPlanSetupSteps/ActionPlanSetupSteps";
import { Card } from "../components/ui/Card/Card";
import { useAuth } from "../hooks/useAuth";
import { useSubscription } from "../hooks/useSubscription";
import useTranslation from "../hooks/useTranslation";
import { Activity } from "../models/activity";
import SurveyDispatchManager from "../models/surveyDispatchManager";
import { ActivityResponse } from "../types/actionPlan";
import { GetSurveyDispatchesResponse } from "../types/survey";
import { SubscriptionActions } from "../types/user";

export const ActionPlanPage = () => {
  const { t: tWorkplaceCulture } = useTranslation("workplaceCulturePage");
  const [activities, setActivities] = useState<Activity[]>([]);
  const [manager, setManager] = useState<SurveyDispatchManager>();

  const { authInfo } = useAuth();
  const { hasAccess } = useSubscription();

  const hasAccessToLaunchSurvey = authInfo.user
    ? hasAccess(SubscriptionActions.LAUNCH_SURVEY)
    : true;

  const actionPlanService = new ActionPlanService();
  const surveyService = new SurveyService();

  const getNow = () => {
    if (import.meta.env.VITE_CURRENT_DATE) {
      return new Date(import.meta.env.VITE_CURRENT_DATE);
    }
    return new Date();
  };
  const now = getNow();

  const {
    data: activitiesData,
    error: activitiesError,
    isLoading: activitiesLoading,
    refetch: handleActivitiesRefetch,
  } = useQuery<ActivityResponse[], AxiosError>(["actionPlanActivities"], () =>
    actionPlanService.getActivities()
  );

  const {
    data: surveyDispatchesData,
    isLoading: surveyDispatchesLoading,
    error: surveyDispatchesError,
  } = useQuery<GetSurveyDispatchesResponse[], AxiosError>(
    ["surveyDispatches"],
    () => surveyService.getDispatches()
  );
  useEffect(() => {
    if (surveyDispatchesData) {
      const newManager = new SurveyDispatchManager(
        surveyDispatchesData,
        tWorkplaceCulture
      );
      setManager(newManager);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyDispatchesData]);

  useEffect(() => {
    if (activitiesData) {
      setActivities(activitiesData.map((activity) => new Activity(activity)));
    }
  }, [activitiesData]);

  // Show setup if no training activities exist
  // Users may have survey activities created independently
  const hasTrainingActivities = activities.some((activity) =>
    activity.isTrainingActivity()
  );

  // Check if there's a survey that is not closed (active, ready, or in progress)
  const hasActiveSurvey = manager
    ? manager.dispatches.some((dispatch) => !dispatch.isClosed())
    : false;

  if (!activitiesData || activities.length === 0 || !hasTrainingActivities) {
    return (
      <ActionPlanSetupSteps
        onComplete={handleActivitiesRefetch}
        hasSurveyAccess={hasAccessToLaunchSurvey}
        hasActiveSurvey={hasActiveSurvey}
        isLoading={surveyDispatchesLoading}
      />
    );
  }
  return (
    <Card>
      <ActionPlanOverview
        activities={activities}
        dispatchManager={manager}
        isLoading={activitiesLoading || surveyDispatchesLoading}
        error={!!surveyDispatchesError || !!activitiesError}
        now={now}
        onActivitiesRefetch={handleActivitiesRefetch}
        hasSurveyAccess={hasAccessToLaunchSurvey}
      />
    </Card>
  );
};
